import { Link } from "@tanstack/react-router";
import { ChevronRight } from "lucide-react";
import { MarketingHeader } from "@/components/marketing/marketing-header";
import { MarketingFooter } from "@/components/marketing/marketing-footer";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { JsonLd, breadcrumbSchema, faqSchema } from "@/components/seo/json-ld";

export type Crumb = { name: string; path: string };
export type FaqItem = { q: string; a: string };

export type ContentSection = {
  heading: string;
  body?: string;
  bullets?: string[];
};

type Props = {
  eyebrow?: string;
  title: string;
  lead: string;
  tldr?: string;
  breadcrumbs: Crumb[];
  sections: ContentSection[];
  faq?: FaqItem[];
  cta?: { label: string; to: string };
  updated?: string;
  heroImage?: string;
  extraSchemas?: Array<Record<string, unknown>>;
};

export function ContentPage({ eyebrow, title, lead, tldr, breadcrumbs, sections, faq, cta, updated, heroImage, extraSchemas }: Props) {
  return (
    <div className="min-h-screen bg-background">
      <JsonLd
        data={[
          breadcrumbSchema(breadcrumbs),
          ...(faq && faq.length > 0 ? [faqSchema(faq)] : []),
          ...(extraSchemas ?? []),
        ]}
      />
      <MarketingHeader />
      <main className="pt-32 pb-24">
        <article className="mx-auto max-w-3xl px-4 sm:px-6">
          <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
            {breadcrumbs.map((b, i) => (
              <span key={b.path} className="inline-flex items-center gap-1.5">
                {i > 0 && <ChevronRight className="h-3 w-3" />}
                {i === breadcrumbs.length - 1 ? (
                  <span className="text-foreground">{b.name}</span>
                ) : (
                  <Link to={b.path as never} className="hover:text-foreground">{b.name}</Link>
                )}
              </span>
            ))}
          </nav>

          {eyebrow && (
            <div className="mb-4 flex items-center gap-3">
              <span className="h-px w-8 bg-border" aria-hidden />
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">{eyebrow}</p>
            </div>
          )}
          <h1 className="font-editorial text-[34px] font-semibold leading-[1.08] tracking-[-0.01em] text-foreground sm:text-5xl text-balance">
            {title}
          </h1>
          <p className="mt-5 text-lg leading-relaxed text-muted-foreground text-balance">{lead}</p>

          {heroImage && (
            <div className="mt-10 overflow-hidden rounded-2xl border border-border">
              <img src={heroImage} alt={title} className="w-full h-auto object-cover aspect-[16/9]" loading="lazy" decoding="async" />
            </div>
          )}

          {tldr && (
            <aside className="mt-8 border-y border-border py-5">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">In sintesi</p>
              <p className="mt-2 text-sm leading-relaxed text-foreground">{tldr}</p>
            </aside>
          )}

          <div className="mt-12 space-y-12">
            {sections.map((s, si) => (
              <section key={s.heading} className="border-t border-border pt-8">
                <div className="mb-3 flex items-center gap-3">
                  <span className="font-editorial text-sm italic text-accent">
                    {String(si + 1).padStart(2, "0")}
                  </span>
                  <span className="h-px w-8 bg-border" aria-hidden />
                </div>
                <h2 className="font-editorial text-2xl font-semibold leading-[1.15] tracking-[-0.01em] text-foreground sm:text-[32px]">
                  {s.heading}
                </h2>
                {s.body && (
                  <p className="mt-3 leading-relaxed text-muted-foreground">{s.body}</p>
                )}
                {s.bullets && s.bullets.length > 0 && (
                  <ul className="mt-4 space-y-2">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex gap-3 text-sm leading-relaxed text-muted-foreground">
                        <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-accent" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          {faq && faq.length > 0 && (
            <section className="mt-16 border-t border-border pt-8">
              <div className="mb-3 flex items-center gap-3">
                <span className="font-editorial text-sm italic text-accent">FAQ</span>
                <span className="h-px w-8 bg-border" aria-hidden />
              </div>
              <h2 className="font-editorial text-2xl font-semibold leading-[1.15] tracking-[-0.01em] text-foreground sm:text-[32px]">
                Domande frequenti
              </h2>
              <Accordion type="single" collapsible className="mt-6">
                {faq.map((f, i) => (
                  <AccordionItem key={i} value={`q-${i}`} className="border-b border-border">
                    <AccordionTrigger className="text-left text-base font-medium">{f.q}</AccordionTrigger>
                    <AccordionContent className="text-sm leading-relaxed text-muted-foreground">{f.a}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {cta && (
            <div className="mt-14 rounded-2xl border border-border bg-card p-8 text-center">
              <h3 className="font-display text-xl font-semibold text-foreground">Prova LexAIPro oggi</h3>
              <p className="mt-2 text-sm text-muted-foreground">14 giorni gratis. Nessuna carta richiesta.</p>
              <Button asChild className="mt-5 rounded-full">
                <Link to={cta.to as never}>{cta.label}</Link>
              </Button>
            </div>
          )}

          {updated && (
            <p className="mt-10 text-xs text-muted-foreground">Ultimo aggiornamento: {updated}</p>
          )}
        </article>
      </main>
      <MarketingFooter />
    </div>
  );
}
